import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import { useSocket } from '../../context/SocketContext';
import api from '../../utils/api';
import { FiBell, FiClock, FiCheckCircle, FiRefreshCw } from 'react-icons/fi';

function StaffNotifications() {
  const { alert } = useModal();
  const socket = useSocket();

  const [waiterCalls, setWaiterCalls] = useState([]);
  const [readyDishes, setReadyDishes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completingCallId, setCompletingCallId] = useState(null);
  const [servingDishId, setServingDishId] = useState(null);

  const fetchWaiterCalls = async () => {
    try {
      const response = await api.get('/api/waiter-calls?status=pending');
      setWaiterCalls(response.data?.waiterCalls || []);
    } catch (error) {
      console.error('Failed to fetch waiter calls:', error);
      setWaiterCalls([]);
    }
  };

  const fetchReadyDishes = async () => {
    try {
      const response = await api.get('/api/pos/orders/active');
      const orders = response.data?.orders || [];

      const flattenedReadyItems = [];
      orders.forEach((order) => {
        (order.order_items || []).forEach((item) => {
          const itemStatus = (item.status || '').toLowerCase();
          if (itemStatus === 'ready') {
            flattenedReadyItems.push({
              id: item.id,
              orderId: order.id,
              orderNumber: order.order_number,
              tableNumber: order.tables?.table_number || 'N/A',
              dishName: item.menu_items?.name || 'Item',
              quantity: item.quantity || 1,
              specialInstructions: item.special_instructions || '',
              readyTime: item.ready_time || item.updated_at || item.created_at,
            });
          }
        });
      });

      // Most recent first
      flattenedReadyItems.sort((a, b) => new Date(b.readyTime) - new Date(a.readyTime));
      setReadyDishes(flattenedReadyItems);
    } catch (error) {
      console.error('Failed to fetch ready dishes:', error);
      setReadyDishes([]);
    }
  };

  const refreshAll = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchWaiterCalls(), fetchReadyDishes()]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshAll();
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      refreshAll();
    }, 5000);

    const handleVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        refreshAll();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onOrderUpdated = () => fetchReadyDishes();
    const onKdsUpdated = () => fetchReadyDishes();

    socket.on('order-updated', onOrderUpdated);
    socket.on('kds-updated', onKdsUpdated);

    return () => {
      socket.off('order-updated', onOrderUpdated);
      socket.off('kds-updated', onKdsUpdated);
    };
  }, [socket]);

  const markWaiterCallDone = async (callId) => {
    try {
      setCompletingCallId(callId);
      await api.patch(`/api/waiter-calls/${callId}/status`, { status: 'completed' });
      await fetchWaiterCalls();
    } catch (error) {
      console.error('Failed to mark waiter call completed:', error);
      await alert('Failed to mark waiter call as done', 'Error', 'error');
    } finally {
      setCompletingCallId(null);
    }
  };

  const markDishServed = async (dish) => {
    try {
      setServingDishId(dish.id);
      await api.patch(`/api/pos/orders/items/${dish.id}/served`);
      await fetchReadyDishes();
      if (socket) {
        socket.emit('order-updated', { orderId: dish.orderId, itemId: dish.id, status: 'served' });
        socket.emit('kds-updated');
      }
    } catch (error) {
      console.error('Failed to mark dish as served:', error);
      await alert('Failed to mark dish as served', 'Error', 'error');
    } finally {
      setServingDishId(null);
    }
  };

  const getMinutesAgo = (timestamp) => {
    if (!timestamp) return 0;
    let time;
    if (typeof timestamp === 'string') {
      // If backend timestamp has no timezone suffix, treat it as UTC
      const hasTimezone = timestamp.includes('Z') || timestamp.includes('+') || timestamp.match(/-\d{2}:\d{2}$/);
      time = new Date(hasTimezone ? timestamp : `${timestamp}Z`);
    } else {
      time = new Date(timestamp);
    }

    if (isNaN(time.getTime())) return 0;

    const diffMs = Date.now() - time.getTime();
    return Math.max(0, Math.floor(diffMs / 60000));
  };

  const totalNotifications = useMemo(() => waiterCalls.length + readyDishes.length, [waiterCalls.length, readyDishes.length]);

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-600">Table calls and ready dishes to serve</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 font-semibold">
              {totalNotifications} pending
            </span>
            <button
              onClick={refreshAll}
              className="p-2 rounded-lg border border-gray-300 hover:bg-gray-50"
              title="Refresh"
            >
              <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Waiter Calls */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Table Waiter Calls</h2>
              <span className="text-xs text-gray-500">{waiterCalls.length} pending</span>
            </div>

            {waiterCalls.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">No pending table calls</div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {waiterCalls.map((call) => {
                  const minutes = getMinutesAgo(call.requested_at);
                  return (
                    <div key={call.id} className="flex items-center justify-between p-3 rounded-lg border border-red-200 bg-red-50">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="bg-red-500 rounded-full p-1.5">
                          <FiBell className="text-white text-sm" />
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">Table {call.tables?.table_number || call.table_number || 'N/A'}</p>
                          <p className="text-xs text-gray-600 flex items-center gap-1">
                            <FiClock className="h-3 w-3" /> {minutes === 0 ? 'just now' : `${minutes}m ago`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => markWaiterCallDone(call.id)}
                        disabled={completingCallId === call.id}
                        className="bg-green-600 text-white px-3 py-1.5 rounded-lg hover:bg-green-700 text-xs font-semibold disabled:opacity-60"
                      >
                        {completingCallId === call.id ? '...' : 'Done'}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Ready Dishes */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Ready Dishes to Serve</h2>
              <span className="text-xs text-gray-500">{readyDishes.length} ready</span>
            </div>

            {readyDishes.length === 0 ? (
              <div className="text-sm text-gray-500 py-8 text-center">No ready dishes right now</div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1">
                {readyDishes.map((dish) => (
                  <div key={dish.id} className="p-3 rounded-lg border border-blue-200 bg-blue-50">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-gray-900 line-clamp-1">
                          {dish.quantity}x {dish.dishName}
                        </p>
                        <p className="text-xs text-gray-700 mt-0.5">
                          Table {dish.tableNumber} • Order #{dish.orderNumber}
                        </p>
                        {dish.specialInstructions ? (
                          <p className="text-xs text-gray-600 italic mt-1">
                            Note: {dish.specialInstructions}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-700 font-semibold">
                          Ready
                        </span>
                        <button
                          onClick={() => markDishServed(dish)}
                          disabled={servingDishId === dish.id}
                          className="text-xs px-2.5 py-1.5 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-60"
                        >
                          {servingDishId === dish.id ? '...' : 'Served'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default StaffNotifications;
